import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Cell,
} from "recharts";
import {
  RefreshCw, Activity, Database, Server, Cpu, AlertTriangle, CheckCircle2, AlertCircle, ExternalLink, DollarSign, TrendingUp, TrendingDown,
} from "lucide-react";
import api from "@/lib/api";

const WINDOWS = [
  { label: "15 min", value: 15 },
  { label: "1h", value: 60 },
  { label: "3h", value: 180 },
  { label: "12h", value: 720 },
];

const ALARM_BADGE = {
  OK: { variant: "outline", icon: CheckCircle2, classes: "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" },
  ALARM: { variant: "destructive", icon: AlertCircle, classes: "border-red-500/40 text-red-400 bg-red-500/10" },
  INSUFFICIENT_DATA: { variant: "outline", icon: AlertTriangle, classes: "border-yellow-500/40 text-yellow-400 bg-yellow-500/10" },
};

const formatBytes = (b) => {
  if (b == null) return "—";
  const gb = b / (1024 ** 3);
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  const mb = b / (1024 ** 2);
  return `${mb.toFixed(0)} MB`;
};

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });
};

const Chart = ({ title, data, color = "#3b82f6", unit = "", threshold, thresholdLabel, capacity, capacityLabel, formatter }) => {
  const fmt = formatter || ((v) => `${v?.toFixed?.(2) ?? v}${unit}`);
  return (
    <Card className="bg-black/20 border-white/10" data-testid={`monitoring-chart-${title.toLowerCase().replace(/\s+/g,'-')}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
          <span>{title}</span>
          {capacity != null && (
            <span className="text-[10px] text-emerald-400/70 font-normal" data-testid={`monitoring-capacity-${title.toLowerCase().replace(/\s+/g,'-')}`}>
              limite: {capacityLabel || fmt(capacity)}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="t" tick={{ fill: "#9ca3af", fontSize: 10 }} tickFormatter={formatTime}
              minTickGap={50} />
            <YAxis tick={{ fill: "#9ca3af", fontSize: 10 }} tickFormatter={(v) => fmt(v).replace(unit, "")}
              domain={capacity != null ? [0, (dataMax) => Math.max(dataMax, capacity * 1.05)] : ['auto','auto']} />
            <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)" }}
              labelFormatter={(t) => new Date(t).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}
              formatter={(v) => [fmt(v), title]} />
            {threshold != null && (
              <ReferenceLine y={threshold} stroke="#f97316" strokeDasharray="4 2"
                label={{ value: thresholdLabel, position: "right", fill: "#f97316", fontSize: 10 }} />
            )}
            {capacity != null && (
              <ReferenceLine y={capacity} stroke="#10b981" strokeDasharray="2 4"
                label={{ value: capacityLabel || `Limite ${fmt(capacity)}`, position: "insideTopRight", fill: "#10b981", fontSize: 10 }} />
            )}
            <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default function AdminMonitoring() {
  const [db, setDb] = useState("kredz-prod-pg");
  const [windowMin, setWindowMin] = useState(60);
  const [data, setData] = useState(null);
  const [infra, setInfra] = useState(null);
  const [awsCosts, setAwsCosts] = useState(null);
  const [awsCostsError, setAwsCostsError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/admin/monitoring/health`, { params: { db, minutes: windowMin } });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Falha ao carregar métricas");
    } finally {
      setLoading(false);
    }
  }, [db, windowMin]);

  const fetchInfra = useCallback(async () => {
    try {
      const r = await api.get(`/admin/monitoring/infra`);
      setInfra(r.data);
    } catch {
      // non-critical, no error toast
    }
  }, []);

  const fetchAwsCosts = useCallback(async () => {
    setAwsCostsError(null);
    try {
      const r = await api.get(`/admin/monitoring/aws-costs`);
      setAwsCosts(r.data);
    } catch (e) {
      setAwsCostsError(e?.response?.data?.detail || "Falha ao carregar custos AWS");
    }
  }, []);

  // fetchHealth só muda de identidade quando db/windowMin mudam (useCallback acima);
  // fetchInfra/fetchAwsCosts são estáveis. Os efeitos disparam exatamente como antes
  // (mount + troca de db/janela), agora com deps exaustivas.
  useEffect(() => { fetchHealth(); }, [fetchHealth]);
  useEffect(() => { fetchInfra(); fetchAwsCosts(); }, [fetchInfra, fetchAwsCosts]);
  useEffect(() => {
    const id = setInterval(() => { fetchHealth(); fetchInfra(); fetchAwsCosts(); }, 60_000);
    return () => clearInterval(id);
  }, [fetchHealth, fetchInfra, fetchAwsCosts]);

  const composite = data?.composite;
  const alarms = data?.alarms || [];
  const inAlarm = alarms.filter(a => a.state === "ALARM").length;
  const m = data?.metrics || {};
  const lim = data?.limits || {};

  const memoryFmt = useMemo(() => (m.freeable_memory_bytes || []).map(p => ({ ...p, v: p.v / (1024 ** 2) })), [m.freeable_memory_bytes]);
  const storageFmt = useMemo(() => (m.free_storage_bytes || []).map(p => ({ ...p, v: p.v / (1024 ** 3) })), [m.free_storage_bytes]);
  const memoryCapMB = lim.freeable_memory_max_bytes ? lim.freeable_memory_max_bytes / (1024 ** 2) : null;
  const storageCapGB = lim.free_storage_max_bytes ? lim.free_storage_max_bytes / (1024 ** 3) : null;

  return (
    <div className="p-6 space-y-6 max-w-screen-2xl mx-auto" data-testid="admin-monitoring">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-400" /> Saúde do Sistema
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Métricas em tempo real do PostgreSQL de produção & sandbox via AWS CloudWatch.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={db} onValueChange={setDb}>
            <TabsList className="bg-black/30 border border-white/10">
              <TabsTrigger value="kredz-prod-pg" data-testid="monitoring-db-prod">Produção</TabsTrigger>
              <TabsTrigger value="kredz-staging-pg" data-testid="monitoring-db-staging">Staging</TabsTrigger>
            </TabsList>
          </Tabs>
          <Tabs value={String(windowMin)} onValueChange={(v) => setWindowMin(Number(v))}>
            <TabsList className="bg-black/30 border border-white/10">
              {WINDOWS.map(w => (
                <TabsTrigger key={w.value} value={String(w.value)} data-testid={`monitoring-window-${w.value}`}>{w.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Button variant="outline" size="icon" onClick={fetchHealth} disabled={loading}
            className="border-white/10 bg-black/20" data-testid="monitoring-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-4 border border-red-500/30 bg-red-500/10 rounded-md text-sm text-red-400" data-testid="monitoring-error">
          {error}
        </div>
      )}

      {data?.instance && (
        <Card className="bg-gradient-to-br from-black/40 to-blue-950/20 border-white/10">
          <CardContent className="p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Stat icon={Database} label="Status" value={data.instance.status} highlight={data.instance.status === "available" ? "ok" : "warn"} />
              <Stat icon={Server} label="Instance" value={data.instance.instance_class} />
              <Stat icon={Cpu} label="Engine" value={`${data.instance.engine} ${data.instance.engine_version}`} />
              <Stat icon={Server} label="Multi-AZ" value={data.instance.multi_az ? "Sim" : "Não"} highlight={data.instance.multi_az ? "ok" : "neutral"} />
              <Stat icon={Database} label="Storage" value={`${data.instance.storage_gb} GB`} />
              <Stat icon={Server} label="Acesso público" value={data.instance.publicly_accessible ? "Sim" : "Não"} highlight={data.instance.publicly_accessible ? "warn" : "ok"} />
            </div>
          </CardContent>
        </Card>
      )}

      {db === "kredz-prod-pg" && (
        <Card className={`border ${composite?.state === "ALARM" ? "bg-red-500/10 border-red-500/40" : "bg-black/20 border-white/10"}`}>
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              {composite?.state === "ALARM" ? (
                <AlertCircle className="w-6 h-6 text-red-400" />
              ) : (
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              )}
              <div>
                <div className="font-medium text-sm">
                  {composite?.state === "ALARM"
                    ? "DEGRADAÇÃO DETECTADA — múltiplos alarmes ativos"
                    : `Sistema saudável — ${alarms.length - inAlarm}/${alarms.length} alarmes em OK`}
                </div>
                <div className="text-xs text-muted-foreground">
                  {composite?.rule_summary || "Composite alarm: ≥2 of 8 component alarms in ALARM state"}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="monitoring-alarm-badges">
              {alarms.map(a => {
                const s = ALARM_BADGE[a.state] || ALARM_BADGE.INSUFFICIENT_DATA;
                const Icon = s.icon;
                return (
                  <span key={a.name} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border ${s.classes}`}
                    title={`${a.description || a.name}\n${a.reason || ""}`}>
                    <Icon className="w-3 h-3" />
                    {a.name.replace("kredz-prod-pg-", "")}
                  </span>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {infra && (
        <Card className="bg-black/20 border-white/10" data-testid="monitoring-infra-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Server className="w-4 h-4 text-blue-400" /> Topologia & Auto-scaling
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-4">
            {infra.load_balancers?.length > 0 && (
              <div>
                <div className="text-xs uppercase text-muted-foreground/60 mb-2 tracking-wider">Application Load Balancers</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {infra.load_balancers.map(alb => (
                    <div key={alb.name} className="flex items-center justify-between p-2 rounded-md bg-black/30 border border-white/5 text-xs">
                      <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${alb.state === "active" ? "bg-emerald-400" : "bg-yellow-400"}`} />
                        <span className="font-mono">{alb.name}</span>
                        <span className="text-muted-foreground">{alb.scheme}</span>
                      </div>
                      <span className="text-emerald-400">{alb.state}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {infra.services?.length > 0 && (
              <div>
                <div className="text-xs uppercase text-muted-foreground/60 mb-2 tracking-wider">ECS Services & Auto-scaling</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {infra.services.map(s => {
                    const usagePct = s.max_capacity ? Math.round((s.running / s.max_capacity) * 100) : 0;
                    const barColor = usagePct >= 80 ? "bg-red-500" : usagePct >= 50 ? "bg-yellow-500" : "bg-emerald-500";
                    return (
                      <div key={`${s.cluster}/${s.service}`} className="p-2 rounded-md bg-black/30 border border-white/5 text-xs space-y-1.5"
                        data-testid={`monitoring-svc-${s.service}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-mono">{s.service}</span>
                          <span className="text-muted-foreground">{s.launch_type}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">
                            {s.running}/{s.desired} running
                            {s.min_capacity != null && ` • escala ${s.min_capacity}–${s.max_capacity}`}
                          </span>
                          <span className={`text-[10px] ${s.rollout_state === "COMPLETED" ? "text-emerald-400" : "text-yellow-400"}`}>
                            {s.rollout_state || "—"}
                          </span>
                        </div>
                        {s.max_capacity > 0 && (
                          <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                            <div className={`h-full ${barColor} transition-all`} style={{ width: `${usagePct}%` }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* AWS Cost Estimate */}
      <AwsCostSection costs={awsCosts} error={awsCostsError} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Chart title="CPU %" data={m.cpu_pct || []} color="#3b82f6" unit="%"
               threshold={80} thresholdLabel="Alarm 80%"
               capacity={lim.cpu_pct_max} capacityLabel="100% (max)" />
        <Chart title="Database Connections" data={m.connections || []} color="#10b981"
               threshold={db === "kredz-prod-pg" ? 70 : null} thresholdLabel="Alarm 70"
               capacity={lim.connections_max}
               capacityLabel={lim.connections_max ? `${lim.connections_max} (max RDS)` : null} />
        <Chart title="Read Latency (ms)" data={(m.read_latency_s || []).map(p => ({ ...p, v: p.v * 1000 }))}
               color="#f59e0b" unit="ms" threshold={200} thresholdLabel="Alarm 200ms" />
        <Chart title="Write Latency (ms)" data={(m.write_latency_s || []).map(p => ({ ...p, v: p.v * 1000 }))}
               color="#f97316" unit="ms" threshold={200} thresholdLabel="Alarm 200ms" />
        <Chart title="Read IOPS" data={m.read_iops || []} color="#8b5cf6"
               capacity={lim.read_iops_max}
               capacityLabel={lim.read_iops_max ? `${lim.read_iops_max} IOPS (${lim.storage_type || 'gp3'})` : null} />
        <Chart title="Write IOPS" data={m.write_iops || []} color="#ec4899"
               capacity={lim.write_iops_max}
               capacityLabel={lim.write_iops_max ? `${lim.write_iops_max} IOPS (${lim.storage_type || 'gp3'})` : null} />
        <Chart title="Disk Queue" data={m.disk_queue || []} color="#ef4444"
               threshold={10} thresholdLabel="Alarm 10" />
        <Chart title="Replica Lag (s)" data={m.replica_lag_s || []} color="#6366f1" unit="s"
               threshold={5} thresholdLabel="Alarm 5s" />
        <Chart title="Freeable Memory (MB)" data={memoryFmt} color="#14b8a6" unit=" MB"
               formatter={(v) => `${(v ?? 0).toFixed(0)} MB`}
               threshold={200} thresholdLabel="Alarm 200MB"
               capacity={memoryCapMB}
               capacityLabel={memoryCapMB ? `${(memoryCapMB / 1024).toFixed(1)} GB RAM total` : null} />
        <Chart title="Free Storage (GB)" data={storageFmt} color="#22c55e" unit=" GB"
               formatter={(v) => `${(v ?? 0).toFixed(1)} GB`}
               threshold={5} thresholdLabel="Alarm 5GB"
               capacity={storageCapGB}
               capacityLabel={storageCapGB ? `${storageCapGB.toFixed(0)} GB alocados` : null} />
      </div>

      {data?.fetched_at && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div>
            Atualizado em {new Date(data.fetched_at).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })} • janela: {data.window_minutes} min •
            auto-refresh 60s
          </div>
          <a href={`https://sa-east-1.console.aws.amazon.com/cloudwatch/home?region=sa-east-1#dashboards:name=kredz-prod-pg-dashboard`}
             target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
             data-testid="monitoring-cloudwatch-link">
            Abrir dashboard CloudWatch <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, highlight }) => {
  const color = highlight === "ok" ? "text-emerald-400"
    : highlight === "warn" ? "text-yellow-400"
    : highlight === "alarm" ? "text-red-400" : "text-foreground";
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`text-sm font-medium ${color}`}>{value || "—"}</div>
    </div>
  );
};


const MONTH_LABELS = {
  "01": "Jan", "02": "Fev", "03": "Mar", "04": "Abr", "05": "Mai", "06": "Jun",
  "07": "Jul", "08": "Ago", "09": "Set", "10": "Out", "11": "Nov", "12": "Dez",
};

const formatMonth = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return `${MONTH_LABELS[m] || m}/${y.slice(2)}`;
};

const formatUSD = (v) => `$ ${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AwsCostSection = ({ costs, error }) => {
  if (error) {
    return (
      <Card className="bg-black/20 border-yellow-500/30" data-testid="aws-cost-error">
        <CardContent className="p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0" />
          <div className="text-sm text-yellow-300">{error}</div>
        </CardContent>
      </Card>
    );
  }
  if (!costs) return null;

  const { current_month, evolution } = costs;
  const prevMonth = evolution.length >= 2 ? evolution[evolution.length - 2] : null;
  const trend = prevMonth && prevMonth.cost > 0
    ? ((current_month.total - prevMonth.cost) / prevMonth.cost) * 100
    : null;
  const trendUp = trend !== null && trend > 0;

  const BAR_COLORS = ["#334155", "#334155", "#334155", "#334155", "#334155", "#3b82f6"];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="aws-cost-section">
      {/* Total Card */}
      <Card className="bg-gradient-to-br from-blue-950/40 to-black/40 border-blue-500/20 lg:col-span-1" data-testid="aws-cost-total-card">
        <CardContent className="p-5 flex flex-col justify-between h-full">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
            <DollarSign className="w-4 h-4 text-blue-400" />
            <span>Estimativa AWS — {formatMonth(current_month.period)}</span>
            {current_month.is_partial && (
              <Badge variant="outline" className="text-[10px] border-blue-500/30 text-blue-400 bg-blue-500/10 ml-auto">
                parcial
              </Badge>
            )}
          </div>
          <div className="text-3xl font-bold tracking-tight text-blue-100" data-testid="aws-cost-total-value">
            {formatUSD(current_month.total)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">{current_month.currency}</div>
          {current_month.forecast != null ? (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5" data-testid="aws-cost-forecast">
              <div className="text-xs text-muted-foreground">Previsão fim do mês:</div>
              <div className="text-sm font-semibold text-blue-300">{formatUSD(current_month.forecast)}</div>
            </div>
          ) : current_month.is_partial && (
            <div className="mt-3 pt-3 border-t border-white/5 text-[10px] text-muted-foreground/60" data-testid="aws-cost-forecast-unavailable">
              Previsão indisponível — histórico insuficiente
            </div>
          )}
          {trend !== null && (
            <div className={`flex items-center gap-1 mt-3 text-xs ${trendUp ? "text-red-400" : "text-emerald-400"}`}
              data-testid="aws-cost-trend">
              {trendUp ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
              <span>{trendUp ? "+" : ""}{trend.toFixed(1)}% vs mês anterior{current_month.is_partial ? " (parcial)" : ""}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Evolution Chart */}
      <Card className="bg-black/20 border-white/10 lg:col-span-2" data-testid="aws-cost-evolution-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-blue-400" /> Evolução do Custo AWS (6 meses)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={evolution} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="month" tick={{ fill: "#9ca3af", fontSize: 11 }} tickFormatter={formatMonth} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
              <Tooltip
                contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)" }}
                labelFormatter={(m) => formatMonth(m)}
                formatter={(v) => [formatUSD(v), "Custo"]}
              />
              <Bar dataKey="cost" radius={[4, 4, 0, 0]} maxBarSize={48}>
                {evolution.map((_, i) => (
                  <Cell key={i} fill={i === evolution.length - 1 ? "#3b82f6" : "#334155"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};
